import React from 'react'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'

/**
 * Shared core resource fields: Name, Authors, Link, and Date.
 * Used by admin modals and creation forms.
 *
 * Props:
 *   name, setName, nameError, setNameError, nameHelper, setNameHelper
 *   authors, setAuthors, authorsError, setAuthorsError, authorsHelper, setAuthorsHelper
 *   link, setLink, linkError, setLinkError, linkHelper, setLinkHelper
 *   date, setDate, dateError, setDateError, dateHelper, setDateHelper
 *   validate — validation function called onBlur
 *   linkValidate — onBlur validation for the Link field only (defaults to `validate`).
 *       Pass the same stricter helper the caller's submit path uses, so a blur
 *       re-runs that check instead of clearing its error (issue #390).
 *   nameLabel — label for the name field (default "Name")
 *   authorsLabel — label for the authors field (default "Authors")
 *   dateLabel — label for the date field (default "Publish Date")
 *   showCharCount — whether to show character counts in helper text (default false)
 *   nameMaxLength — max char count for name field (optional)
 *   authorsMaxLength — max char count for authors field (optional)
 */
const CoreResourceFields = ({
    name, setName, nameError, setNameError, nameHelper, setNameHelper,
    authors, setAuthors, authorsError, setAuthorsError, authorsHelper, setAuthorsHelper,
    link, setLink, linkError, setLinkError, linkHelper, setLinkHelper,
    date, setDate, dateError, setDateError, dateHelper, setDateHelper,
    validate,
    // Must be destructured AFTER `validate` -- the default reads it.
    linkValidate = validate,
    nameLabel = 'Name',
    authorsLabel = 'Authors',
    dateLabel = 'Publish Date',
    showCharCount = false,
    nameMaxLength,
    authorsMaxLength,
}) => {
    return (
        <>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    value={name}
                    onChange={e => setName(e.target.value)}
                    label={nameLabel}
                    required
                    error={nameError}
                    helperText={nameHelper || (showCharCount && nameMaxLength ? `${name.length}/${nameMaxLength} characters` : '')}
                    onBlur={() => validate(name, setNameError, setNameHelper, nameMaxLength, nameLabel)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    value={authors}
                    onChange={e => setAuthors(e.target.value)}
                    label={authorsLabel}
                    required
                    error={authorsError}
                    helperText={authorsHelper || (showCharCount && authorsMaxLength ? `${authors.length}/${authorsMaxLength} characters` : '')}
                    onBlur={() => validate(authors, setAuthorsError, setAuthorsHelper, authorsMaxLength, authorsLabel)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    value={link}
                    type='url'
                    onChange={e => setLink(e.target.value)}
                    label="Link to Resource"
                    required
                    error={linkError}
                    helperText={linkHelper}
                    onBlur={() => linkValidate(link, setLinkError, setLinkHelper)}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    value={date}
                    type='date'
                    onChange={e => setDate(e.target.value)}
                    label={dateLabel}
                    required
                    InputLabelProps={{ shrink: true }}
                    error={dateError}
                    helperText={dateHelper}
                    onBlur={() => validate(date, setDateError, setDateHelper)}
                />
            </Grid>
        </>
    )
}

export default CoreResourceFields
